/**
 * ConvitesPendentesCard — mostra a fila de convites de transferência para
 * emails que ainda não tinham cadastro no momento do convite. Cada linha:
 *   - Email + tempo desde o envio
 *   - Se o convidado JÁ SE CADASTROU → botão "Refazer transferência agora"
 *     (executa POST /transferir-responsavel novamente).
 *   - Sempre: botão "Cancelar convite".
 *
 * Consome:
 *   - GET  /api/org-projects/:orgId/convites-transferencia
 *   - DELETE /api/org-projects/:orgId/convites-transferencia/:conviteId
 *   - POST /api/org-projects/:orgId/transferir-responsavel  (executar)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Mail, Clock, UserCheck, XCircle, Loader2, ArrowRightLeft, MailPlus } from 'lucide-react';
import { api } from '../services/api';

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'agora há pouco';
  if (mins < 60) return `há ${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `há ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `há ${d} dia${d > 1 ? 's' : ''}`;
  return `há ${Math.floor(d / 30)} mês(es)`;
}

export default function ConvitesPendentesCard({ orgId, onTransferred }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executando, setExecutando] = useState(null); // id do convite em execução
  const [cancelando, setCancelando] = useState(null);

  const carregar = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await api.get(`/org-projects/${orgId}/convites-transferencia`);
      setItems(res.data?.items || []);
    } catch (_e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { carregar(); }, [carregar]);

  const executar = async (convite) => {
    setExecutando(convite.id);
    try {
      const res = await api.post(`/org-projects/${orgId}/transferir-responsavel`, {
        novo_responsavel: convite.email_convidado,
      });
      const novo = res.data?.novo_responsavel;
      toast.success('Transferência concluída.');
      // Cancela o convite (não é mais pendente)
      try {
        await api.delete(`/org-projects/${orgId}/convites-transferencia/${convite.id}`);
      } catch (_e) { /* ignora — o efeito principal já ocorreu */ }
      onTransferred?.(novo);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Falha ao transferir.';
      toast.error(detail);
    } finally {
      setExecutando(null);
      carregar();
    }
  };

  const cancelar = async (convite) => {
    setCancelando(convite.id);
    try {
      await api.delete(`/org-projects/${orgId}/convites-transferencia/${convite.id}`);
      toast.success('Convite cancelado.');
      setItems((prev) => prev.filter((c) => c.id !== convite.id));
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Falha ao cancelar.';
      toast.error(detail);
    } finally {
      setCancelando(null);
    }
  };

  if (!orgId) return null;

  return (
    <div
      className="bg-white rounded-xl shadow-sm p-6 border border-blue-100"
      data-testid="convites-pendentes-card"
    >
      <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2">
        <MailPlus className="w-5 h-5 text-blue-600" />
        Convites pendentes
        {items.length > 0 && (
          <span className="ml-2 text-xs font-semibold px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
            {items.length}
          </span>
        )}
      </h2>
      <p className="text-gray-500 text-sm mb-4">
        Pessoas que você convidou para assumir a responsabilidade, mas que
        ainda não haviam se cadastrado. Assim que se cadastrarem, clique em
        <strong> Refazer transferência</strong> para efetivar.
      </p>

      {loading ? (
        <div className="text-center py-6 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
          Carregando convites…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-6 text-gray-400 text-sm" data-testid="convites-vazio">
          Nenhum convite pendente.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((c) => (
            <li
              key={c.id}
              className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-blue-200 transition-colors"
              data-testid={`convite-item-${c.id}`}
            >
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                {c.ja_cadastrado ? (
                  <UserCheck className="w-4 h-4 text-green-600" />
                ) : (
                  <Mail className="w-4 h-4 text-blue-600" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-800 truncate" data-testid={`convite-email-${c.id}`}>
                  {c.email_convidado}
                </p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Convidado {relativeTime(c.created_at)}
                  {c.ja_cadastrado && (
                    <span className="ml-2 text-green-600 font-medium">• já se cadastrou</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {c.ja_cadastrado && (
                  <button
                    type="button"
                    onClick={() => executar(c)}
                    disabled={executando === c.id}
                    className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                    data-testid={`btn-refazer-${c.id}`}
                    title="Executar transferência agora"
                  >
                    {executando === c.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <ArrowRightLeft className="w-3 h-3" />
                    )}
                    Refazer
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => cancelar(c)}
                  disabled={cancelando === c.id}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                  data-testid={`btn-cancelar-convite-${c.id}`}
                  title="Cancelar convite"
                >
                  {cancelando === c.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
